import { cn } from "@tcg/ui";
import {
  ArrowDownToLine,
  HandCoins,
  PackageOpen,
  PauseCircle,
  Receipt,
  Settings,
  ShoppingCart,
  Stamp,
  Store,
  Undo2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";

export interface NavItem {
  to: string;
  label: string;
  icon: typeof ShoppingCart;
}

export const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "nav.sale", icon: ShoppingCart },
  { to: "/hold", label: "nav.hold", icon: PauseCircle },
  { to: "/returns", label: "nav.return", icon: Undo2 },
  { to: "/pickup", label: "nav.pickup", icon: PackageOpen },
  { to: "/tax-free", label: "nav.tax_free", icon: Stamp },
  { to: "/buylist", label: "nav.buylist", icon: HandCoins },
  { to: "/consignment", label: "nav.consignment", icon: ArrowDownToLine },
  { to: "/shift", label: "nav.shift", icon: Store },
  { to: "/receipts", label: "nav.history", icon: Receipt },
  { to: "/settings", label: "nav.settings", icon: Settings },
];

/**
 * The big menu buttons (design/05 §3 — ≥ 56 px): 販売 / 保留 / 返品 / 受取 / 免税 / 買取 / 委託受付 /
 * レジ開閉 + 履歴 + ⚙️. `disabled` = screens that need the server while the register is offline
 * (T07-33): greyed out, with the reason read out and shown on a long press / hover.
 */
export function NavBar({
  hidden = [],
  disabled = [],
  badges = {},
}: {
  hidden?: string[];
  disabled?: string[];
  /** Counts on a button (受取 = web orders waiting at the counter — T07-40). */
  badges?: Readonly<Record<string, number>>;
}) {
  const { t } = useTranslation();
  return (
    <nav
      aria-label={t("nav.label")}
      className="grid auto-cols-fr grid-flow-col gap-1 border-t bg-muted p-1"
    >
      <span id="nav-offline-reason" className="sr-only">
        {t("offline.online_only")}
      </span>
      {NAV_ITEMS.filter((item) => !hidden.includes(item.to)).map(({ to, label, icon: Icon }) =>
        disabled.includes(to) ? (
          <span
            key={to}
            role="link"
            aria-disabled="true"
            aria-describedby="nav-offline-reason"
            title={t("offline.online_only")}
            className="flex min-h-pos-touch cursor-not-allowed flex-col items-center justify-center gap-0.5 rounded-md px-1 text-sm font-medium text-muted-foreground opacity-60 [&_svg]:size-6"
          >
            <Icon aria-hidden />
            <span className="truncate">{t(label)}</span>
          </span>
        ) : (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            className={({ isActive }) =>
              cn(
                "relative flex min-h-pos-touch flex-col items-center justify-center gap-0.5 rounded-md px-1 text-sm font-medium [&_svg]:size-6",
                isActive ? "bg-primary text-primary-foreground" : "hover:bg-background",
              )
            }
          >
            <Icon aria-hidden />
            <span className="truncate">{t(label)}</span>
            {badges[to] ? (
              <span
                data-testid={`nav-badge-${to.slice(1) || "sale"}`}
                className="absolute right-1 top-1 min-w-6 rounded-full bg-pos-problem px-1.5 text-center text-sm font-bold leading-6 text-white"
              >
                <span aria-hidden>{badges[to]}</span>
                <span className="sr-only">{t("nav.badge", { count: badges[to] })}</span>
              </span>
            ) : null}
          </NavLink>
        ),
      )}
    </nav>
  );
}
